import { useState, useContext, useEffect, useCallback } from "react";
import {
  createZonalAdmin,
  getZonalAdmins,
} from "../../api/adminApi";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Card,
  CardContent,
  CircularProgress,
  Box,
  Stack,
  Avatar,
  Snackbar,
  Alert
} from "@mui/material";

import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight'; // 👈 Added arrow icon
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';

export default function ZonalAdmins() {
  const { admin } = useContext(AuthContext);
  const navigate = useNavigate();

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [openCreate, setOpenCreate] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    zone: "",
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showMessage = (msg, type = "success") => {
    setSnackbar({ open: true, message: msg, severity: type });
  };

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getZonalAdmins();
      setAdmins(res.data);
    } catch (err) {
      showMessage("Failed to load zonal admins", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (admin?.role === "CITY_ADMIN") {
      fetchAdmins();
    }
  }, [admin, fetchAdmins]);

  const handleCreate = async () => {
    try {
      setActionLoading(true);
      await createZonalAdmin(form);
      setOpenCreate(false);
      setForm({ name: "", email: "", password: "", zone: "" });
      fetchAdmins();
      showMessage("Zonal Admin created successfully!");
    } catch (err) {
      showMessage(err?.response?.data?.error || "Failed to create admin", "error");
    } finally {
      setActionLoading(false);
    }
  };

 
  const glassCardStyle = {
    borderRadius: '24px',
    border: "1px solid #ECE5DA",
    bgcolor: '#FFFFFF',
    boxShadow: '0 10px 24px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 16px 32px rgba(0, 0, 0, 0.1)',
      borderColor: '#F8C332'
    }
  };

  const modernInputStyle = {
    '& .MuiOutlinedInput-root': { borderRadius: '12px' }
  };

  return (
    <Box sx={{ pb: 10 }}>
      {/* HEADER */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h4" fontWeight="900" sx={{ color: "#3A2C23" }}>
          Zonal Admins
        </Typography>

        {admin?.role === "CITY_ADMIN" && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disableElevation
            onClick={() => {
              setForm({ name: "", email: "", password: "", zone: "" });
              setOpenCreate(true);
            }}
            sx={{
              borderRadius: '16px', py: 1.2, px: 3, bgcolor: '#F8C332', color: '#3A2C23',
              fontWeight: '900', textTransform: 'none', boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)',
              '&:hover': { bgcolor: '#F5B71D' }
            }}
          >
            Add Admin
          </Button>
        )}
      </Box>

      {/* LIST OF ADMINS */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress sx={{ color: "#F8C332" }} />
        </Box>
      ) : admins.length === 0 ? (
        <Box textAlign="center" py={5} color="#A59D96">
          <AdminPanelSettingsIcon sx={{ fontSize: 60, opacity: 0.5, mb: 1 }} />
          <Typography variant="h6" fontWeight="700">No Zonal Admins Found</Typography>
        </Box>
      ) : (
        <Stack spacing={2} mb={4}>
          {admins.map((a) => (
            <Card
              key={a.id}
              onClick={() => navigate(`/zonal-admins/${a.id}`, { state: { adminDetails: a } })}
              sx={glassCardStyle}
              elevation={0}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>

                  {/* LEFT: Avatar & Info */}
                  <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2 }, alignItems: "center", flex: 1, minWidth: 0 }}>
                    <Avatar sx={{ bgcolor: "#FFF6DE", color: "#F29D17", width: 50, height: 50, fontWeight: "900", fontSize: "1.2rem" }}>
                      {a.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" fontWeight="900" sx={{ color: "#3A2C23", lineHeight: 1.2 }} noWrap>{a.name}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, color: "#6E655E" }}>
                        <EmailOutlinedIcon sx={{ fontSize: 16 }} />
                        <Typography variant="body2" fontWeight="600" noWrap>{a.email}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3, color: "#A59D96" }}>
                        <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight="800" sx={{ textTransform: "uppercase" }} noWrap>
                          Zone: {a.zone || "Unassigned"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* RIGHT: Clean right arrow indicator replacing the edit icon */}
                  <ChevronRightIcon sx={{ color: "#A59D96" }} />

                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* CREATE DIALOG */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "24px", p: 1 } }}>
        <DialogTitle sx={{ fontWeight: "900", color: "#3A2C23" }}>Add Zonal Admin</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" fullWidth sx={modernInputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <TextField label="Email" fullWidth sx={modernInputStyle} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <TextField label="Zone" fullWidth sx={modernInputStyle} value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} />
            <TextField label="Password" type="password" fullWidth sx={modernInputStyle} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenCreate(false)} sx={{ color: "#6E655E", fontWeight: "800" }}>Cancel</Button>
          <Button variant="contained" disableElevation onClick={handleCreate} disabled={actionLoading} sx={{ borderRadius: "12px", bgcolor: "#F8C332", color: "#3A2C23", fontWeight: "900", "&:hover": { bgcolor: "#F5B71D" } }}>
            {actionLoading ? <CircularProgress size={24} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: '12px', fontWeight: 'bold' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}